import { ExternalLink } from "lucide-react";
import { lastDatum, visadPeriod, type ChoiceInfo } from "@/lib/municipality-choice";

const RUBRIK: Record<ChoiceInfo["kind"], string> = {
  forskola: "Söka förskoleplats",
  grundskola: "Skolval till grundskolan",
};

/**
 * Kommunens egen sida om skolval eller förskoleplats. Visar bara det vi läst
 * på kommunens sida: länken, e-tjänstens namn och — om kommunen anger den med
 * datum och uppgiften är högst sex månader gammal — perioden, ordagrant.
 * Utan uppgift renderas ingenting.
 */
export function MunicipalityChoice({ items, only }: { items: ChoiceInfo[]; only?: ChoiceInfo["kind"] }) {
  const rows = items.filter((i) => !only || i.kind === only);
  if (rows.length === 0) return null;
  const kommun = rows[0]!.municipality_name;
  return (
    <section aria-labelledby="kommunens-ansokan" className="mt-8 rounded-card border border-border bg-surface p-card">
      <h2 id="kommunens-ansokan" className="text-h4 font-semibold text-ink">
        Så söker du i {kommun}
      </h2>
      <ul className="mt-3 space-y-4">
        {rows.map((r) => {
          const period = visadPeriod(r);
          return (
            <li key={r.kind}>
              <p className="text-small font-semibold text-ink">{RUBRIK[r.kind]}</p>
              {period && (
                <blockquote className="mt-1 border-l-2 border-primary pl-3 text-small text-ink">
                  ”{period}”
                </blockquote>
              )}
              {r.e_service_name && (
                <p className="mt-1 text-small text-ink">
                  E-tjänst:{" "}
                  {r.e_service_url ? (
                    <a href={r.e_service_url} rel="noopener" className="font-medium text-primary underline underline-offset-2">
                      {r.e_service_name}
                    </a>
                  ) : (
                    r.e_service_name
                  )}
                </p>
              )}
              <a
                href={r.page_url}
                rel="noopener"
                className="mt-1 inline-flex min-h-11 items-center gap-1.5 text-small font-medium text-primary underline underline-offset-2"
              >
                {kommun} kommuns sida <ExternalLink className="h-3.5 w-3.5" aria-hidden />
              </a>
              <p className="text-small text-ink-muted">Läst på kommunens sida {lastDatum(r.read_on)}.</p>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
